/**
 * Technology Hub — top-level category hub for /technology.
 * Cross-references content/technology/*.md
 *
 * Lists 10 technology pages:
 *  /technology/ai-ml, /technology/nlp, /technology/computer-vision,
 *  /technology/data, /technology/infrastructure, /technology/real-time-processing,
 *  /technology/predictive-analytics, /reliability, /scalability, /security-architecture
 */

export const technologyHubMeta = {
  title: "Perception X2 Technology Stack | AI, NLP, Vision, Infrastructure",
  description:
    "Ten technology surfaces: AI/ML, NLP, computer vision, data architecture, infrastructure, real-time processing, predictive analytics, reliability, scalability, and security.",
  canonical: "https://perception.ac/technology",
  slug: "technology",
};

export interface TechnologyChild {
  code: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const technologyChildren: TechnologyChild[] = [
  {
    code: "01",
    title: "AI / Machine Learning",
    description:
      "Proprietary transformer architectures, reinforcement learning, and multi-agent systems. 15+ years of model refinement.",
    href: "/technology/ai-ml",
    icon: "Brain",
    accent: "crimson",
  },
  {
    code: "02",
    title: "Natural Language Processing",
    description:
      "Multi-lingual transformer pipelines across 15+ languages. Sentiment, intent, identity, ideology, and narrative preference.",
    href: "/technology/nlp",
    icon: "MessageSquare",
    accent: "purple",
  },
  {
    code: "03",
    title: "Computer Vision",
    description:
      "Visual perception analysis across image, video, and short-form. Brand presence, scene analysis, and visual authenticity scoring.",
    href: "/technology/computer-vision",
    icon: "Eye",
    accent: "cyan",
  },
  {
    code: "04",
    title: "Data Architecture",
    description:
      "Multi-layer data fabric with sub-second telemetry. Petabyte-scale ingestion, lineage, and quality control.",
    href: "/technology/data",
    icon: "Database",
    accent: "emerald",
  },
  {
    code: "05",
    title: "Infrastructure",
    description:
      "Cloud-native distributed infrastructure with cryptographic isolation. Multi-region active-active with sub-millisecond failover.",
    href: "/technology/infrastructure",
    icon: "Server",
    accent: "amber",
  },
  {
    code: "06",
    title: "Real-Time Processing",
    description:
      "Stream processing pipelines at sub-second cadence. Event-first architecture with priority routing and adaptive batching.",
    href: "/technology/real-time-processing",
    icon: "Zap",
    accent: "denim",
  },
  {
    code: "07",
    title: "Predictive Analytics",
    description:
      "Cascade probability modeling, narrative trajectory forecasting, and risk scoring on incoming perception signals.",
    href: "/technology/predictive-analytics",
    icon: "TrendingUp",
    accent: "violet",
  },
  {
    code: "08",
    title: "Reliability",
    description:
      "99.9999% platform uptime. Defense-grade resilience through multi-region active-active, automated failover, and self-healing.",
    href: "/reliability",
    icon: "Shield",
    accent: "rose",
  },
  {
    code: "09",
    title: "Scalability",
    description:
      "Elastic compute from 100 to 1,000,000+ personas. Linear scale-out tested at petabyte volumes with sub-second SLAs.",
    href: "/scalability",
    icon: "Maximize",
    accent: "crimson",
  },
  {
    code: "10",
    title: "Security Architecture",
    description:
      "AES-256, TLS 1.3, zero trust, SOC 2 Type II, ISO 27001. Defense-grade protection — zero incidents in 15+ years.",
    href: "/security-architecture",
    icon: "Lock",
    accent: "emerald",
  },
];

export interface TechnologyStat {
  value: string;
  label: string;
  to: number;
  suffix?: string;
  decimals?: number;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
}

export const technologyStats: TechnologyStat[] = [
  { value: "10", label: "Tech Surfaces", to: 10, icon: "Cpu", accent: "denim" },
  { value: "15+", label: "Languages", to: 15, suffix: "+", icon: "Languages", accent: "purple" },
  { value: "1PB+", label: "Data Fabric", to: 1, suffix: "PB+", decimals: 0, icon: "Database", accent: "emerald" },
  { value: "<1s", label: "Stream Latency", to: 1, suffix: "s", decimals: 0, icon: "Zap", accent: "amber" },
  { value: "99.9999%", label: "Platform Uptime", to: 99.9999, decimals: 4, suffix: "%", icon: "Shield", accent: "crimson" },
];

export interface TechnologyFeature {
  title: string;
  description: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const technologyFeatures: TechnologyFeature[] = [
  {
    title: "Proprietary Transformer Architectures",
    description: "Purpose-built transformer variants for narrative, identity, and emotional modeling — not licensed from third parties.",
    icon: "Brain",
    accent: "crimson",
  },
  {
    title: "Multi-Modal Perception",
    description: "Text, image, and video analyzed through a unified perception framework. Cross-modal consistency verification.",
    icon: "Layers",
    accent: "purple",
  },
  {
    title: "Reinforcement Learning Loop",
    description: "Continuous reinforcement learning from outcome telemetry. The platform improves with every engagement.",
    icon: "RefreshCw",
    accent: "cyan",
  },
  {
    title: "Petabyte Data Fabric",
    description: "Multi-layer data fabric with sub-second telemetry. Lineage, quality control, and audit-grade retention.",
    icon: "Database",
    accent: "emerald",
  },
  {
    title: "Multi-Region Active-Active",
    description: "Distributed infrastructure across multiple regions with sub-millisecond failover and zero-downtime deploys.",
    icon: "Server",
    accent: "amber",
  },
  {
    title: "Sub-Second Stream Processing",
    description: "Event-first architecture with priority routing. Critical signals processed before they reach the dashboard.",
    icon: "Zap",
    accent: "denim",
  },
  {
    title: "Cascade Probability Modeling",
    description: "Forward-looking cascade prediction on incoming signals. Pre-empt narrative crystallization before it occurs.",
    icon: "TrendingUp",
    accent: "violet",
  },
  {
    title: "Defense-Grade Security",
    description: "AES-256, TLS 1.3, zero trust, SOC 2 Type II, ISO 27001. The same standards used by defense and intelligence agencies.",
    icon: "Shield",
    accent: "rose",
  },
];

export interface TechnologyUseCase {
  title: string;
  body: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const technologyUseCases: TechnologyUseCase[] = [
  {
    title: "Multi-Language Engagement",
    body: "Perception telemetry across 15+ languages with cross-language normalization. Reach global audiences without sacrificing precision.",
    icon: "Globe",
    accent: "crimson",
  },
  {
    title: "Multi-Modal Analysis",
    body: "Text, image, and video analyzed through a unified perception framework. Cross-modal consistency verification catches coordinated narratives.",
    icon: "Layers",
    accent: "purple",
  },
  {
    title: "Real-Time Crisis Response",
    body: "Sub-second stream processing ensures crisis-class signals reach the response stack before they crystallize in public consciousness.",
    icon: "Siren",
    accent: "cyan",
  },
  {
    title: "Petabyte-Scale Analytics",
    body: "Multi-layer data fabric handling petabyte volumes with sub-second query response. No historical depth is sacrificed for speed.",
    icon: "Database",
    accent: "emerald",
  },
  {
    title: "Defense-Grade Compliance",
    body: "SOC 2 Type II, ISO 27001, defense and intelligence agency certifications. Audit-grade lineage for every metric.",
    icon: "Shield",
    accent: "amber",
  },
  {
    title: "Sovereign Operations",
    body: "Multi-region active-active deployment with cryptographic isolation. Sovereign-grade data residency for governments and regulated industries.",
    icon: "Lock",
    accent: "denim",
  },
];

export interface TechnologyWhy {
  title: string;
  body: string;
  metric?: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const technologyWhy: TechnologyWhy[] = [
  {
    title: "15+ Years of Refinement",
    body: "Every technology surface has been refined in the world's most demanding engagements — governments, Fortune 500, sovereign operations.",
    metric: "15+",
    accent: "crimson",
  },
  {
    title: "Proprietary Architectures",
    body: "Not licensed, not white-labeled. Every model, every pipeline, every infrastructure component is engineered in-house.",
    metric: "100%",
    accent: "purple",
  },
  {
    title: "Sub-Second Latency",
    body: "End-to-end event processing at sub-second cadence. Critical signals reach the response stack before they crystallize.",
    metric: "<1s",
    accent: "cyan",
  },
  {
    title: "Petabyte Fabric",
    body: "Multi-layer data fabric handling petabyte volumes with sub-second query response. No historical depth sacrificed for speed.",
    metric: "1PB+",
    accent: "emerald",
  },
  {
    title: "99.9999% Uptime",
    body: "Defense-grade resilience through multi-region active-active, automated failover, and self-healing infrastructure.",
    metric: "99.9999%",
    accent: "amber",
  },
  {
    title: "Zero Incidents",
    body: "15+ years of operations without a single security incident. SOC 2 Type II, ISO 27001, defense and intelligence certifications.",
    metric: "0",
    accent: "denim",
  },
];

export interface TechnologyFaq {
  q: string;
  a: string;
}

export const technologyFaq: TechnologyFaq[] = [
  {
    q: "What are the ten Perception X2 technology surfaces?",
    a: "The ten technology surfaces are: AI/ML, NLP, Computer Vision, Data Architecture, Infrastructure, Real-Time Processing, Predictive Analytics, Reliability, Scalability, and Security Architecture. Each is a purpose-built technology pillar that composes the Perception X2 platform.",
  },
  {
    q: "Is the AI/ML stack proprietary or licensed?",
    a: "The Perception X2 AI/ML stack is 100% proprietary. Transformer architectures, reinforcement learning pipelines, and multi-agent systems are all engineered in-house over 15+ years of refinement. No third-party model risk. No white-label dependencies.",
  },
  {
    q: "How many languages does the NLP stack support?",
    a: "The Perception X2 NLP stack supports 15+ languages natively — including English, Mandarin, Spanish, French, German, Portuguese, Arabic, Hindi, Japanese, Korean, Russian, Italian, Dutch, Turkish, and Vietnamese. Additional languages available on request with 4–6 week deployment.",
  },
  {
    q: "What is the latency from signal to action?",
    a: "End-to-end latency from signal detection to autonomous action is sub-second on critical signals, sub-60-seconds on material signals, and 5 minutes on strategic escalations. Real-Time Processing is engineered for sub-second stream latency at petabyte volumes.",
  },
  {
    q: "How is the platform secured?",
    a: "Security is foundational. AES-256 encryption at rest, TLS 1.3 in transit, zero trust access control, cryptographic isolation per engagement, SOC 2 Type II compliance, and ISO 27001 certification. The platform has zero security incidents in 15+ years of operation.",
  },
  {
    q: "How does the platform scale?",
    a: "Elastic compute from 100 to 1,000,000+ personas. Linear scale-out tested at petabyte volumes with sub-second SLAs. Multi-region active-active deployment with sub-millisecond failover. The platform auto-scales against engagement demand.",
  },
  {
    q: "What is the platform uptime?",
    a: "99.9999% uptime — equivalent to 31.5 seconds of downtime per year. Achieved through multi-region active-active deployment, automated failover, self-healing infrastructure, and continuous load testing.",
  },
  {
    q: "Does the technology stack support on-premise deployment?",
    a: "Yes. For sovereign and defense clients, the Perception X2 stack is available as an air-gapped on-premise deployment with the same functionality as the cloud platform. Custom deployment architectures available for highly regulated environments.",
  },
  {
    q: "How does the platform handle petabyte-scale data?",
    a: "The Data Architecture surface is engineered around a multi-layer data fabric with sub-second query response. Petabyte volumes are handled through tiered storage (hot, warm, cold) with intelligent caching, and lineage tracking across every transformation.",
  },
  {
    q: "Is the technology stack available via API?",
    a: "Yes. Every technology surface exposes a documented RESTful API with webhooks for real-time events. SDKs are available in TypeScript, Python, and Go. Enterprise clients can wire individual surfaces directly into existing pipelines.",
  },
];

export const technologyCta = {
  eyebrow: "Engage the Technology Stack",
  title: "Ten technology surfaces. One engineering culture.",
  description:
    "The Perception X2 technology stack is engineered in-house over 15+ years and tested in the world's most demanding environments. Engage the surface that matches your operational requirement — or commission the full stack.",
  primary: { label: "Request Technical Briefing", href: "/contact" },
  secondary: { label: "View API Reference", href: "/platform/api" },
  trustItems: [
    "10 technology surfaces",
    "15+ languages",
    "Petabyte data fabric",
    "99.9999% uptime",
    "Zero security incidents",
  ],
};

/* Tech stack layered diagram data — bespoke visual */
export interface TechLayer {
  layer: number;
  label: string;
  description: string;
  surfaces: string[];
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const techLayers: TechLayer[] = [
  {
    layer: 6,
    label: "Application Layer",
    description: "Engagement surfaces — dashboards, reports, briefings, custom interfaces.",
    surfaces: ["Dashboards", "Reports", "Briefings", "API"],
    accent: "violet",
  },
  {
    layer: 5,
    label: "Intelligence Layer",
    description: "Perception models and analytical engines — 144-dimensional insight.",
    surfaces: ["Cognitive Resonance", "Sentiment Mesh", "Predictive Analytics", "Cascade Modeling"],
    accent: "rose",
  },
  {
    layer: 4,
    label: "AI / ML Layer",
    description: "Proprietary transformer architectures, reinforcement learning, multi-agent systems.",
    surfaces: ["Transformers", "RL Pipelines", "Multi-Agent", "Vision Models"],
    accent: "crimson",
  },
  {
    layer: 3,
    label: "Data Layer",
    description: "Multi-layer data fabric with petabyte-scale ingestion and lineage.",
    surfaces: ["Stream Processing", "Batch", "Lineage", "Quality"],
    accent: "emerald",
  },
  {
    layer: 2,
    label: "Infrastructure Layer",
    description: "Distributed cloud-native infrastructure with multi-region active-active.",
    surfaces: ["Compute", "Storage", "Network", "Failover"],
    accent: "amber",
  },
  {
    layer: 1,
    label: "Security Layer",
    description: "Defense-grade protection — AES-256, TLS 1.3, zero trust, SOC 2 Type II.",
    surfaces: ["Encryption", "Zero Trust", "Audit", "Compliance"],
    accent: "denim",
  },
];

/* SLA metrics — bespoke visual data */
export interface SlaMetric {
  metric: string;
  value: number;
  unit: string;
  target: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const slaMetrics: SlaMetric[] = [
  { metric: "Stream Latency", value: 0.85, unit: "s", target: "< 1s", accent: "crimson" },
  { metric: "API Response (p99)", value: 142, unit: "ms", target: "< 200ms", accent: "purple" },
  { metric: "Dashboard Refresh", value: 1.0, unit: "s", target: "< 1s", accent: "cyan" },
  { metric: "Model Inference", value: 38, unit: "ms", target: "< 50ms", accent: "emerald" },
  { metric: "Failover Time", value: 0.6, unit: "s", target: "< 1s", accent: "amber" },
  { metric: "Backup Window", value: 15, unit: "min", target: "< 30min", accent: "denim" },
];
